import { RemoveRedEyeSharp, VisibilityOff } from "@mui/icons-material";
import { IconButton, SxProps, TextField, Theme } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

export interface NumericPasswordBoxProps {
    password: string;
    onEnter?: () => void;
    onPasswordChange: (event: string) => void;
    size?: "small" | "medium";
    sx?: SxProps<Theme>;
    fullWidth?: boolean;
    label?: string;
}

export default function NumericPasswordBox(props: Readonly<NumericPasswordBoxProps>) {
    const { password, onPasswordChange, onEnter, size, fullWidth, sx, label } = props;
    const { t } = useTranslation();

    const [passwordMode, setPasswordMode] = useState<"text" | "password">("password");
    const [passwordHasValue, setPasswordHasValue] = useState(false);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && onEnter) {
            onEnter();
        }
    };

    const onPassChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        if (/^\d*$/.test(newValue)) { // Only allow numeric input
            onPasswordChange(newValue);
        }
    };

    const makeAnimationStartHandler = (stateSetter: React.Dispatch<React.SetStateAction<boolean>>) => (e: React.AnimationEvent<HTMLInputElement>) => {
        const autofilled = !!(e.target as HTMLInputElement)?.matches("*:-webkit-autofill");
        if (e.animationName === "mui-auto-fill" || e.animationName === "mui-auto-fill-cancel") {
            stateSetter(autofilled);
        }
    };

    return (
        <TextField
            sx={sx}
            fullWidth={fullWidth}
            size={size ?? "medium"}
            type={passwordMode}
            inputProps={{
                inputMode: "numeric", // Opens numeric keyboard on mobile
                pattern: "[0-9]*", // Ensures only numbers
                autoComplete: "one-time-code", // Helps with iOS autofill
                onAnimationStart: makeAnimationStartHandler(setPasswordHasValue),
            }}
            InputLabelProps={{
                shrink: passwordHasValue || password.length > 0,
            }}
            variant="outlined"
            value={password}
            onChange={onPassChange}
            id="outlined-numeric-password-input"
            label={label ?? t("login.enterPassword")}
            onKeyDown={handleKeyPress}
            InputProps={{
                endAdornment: (
                    <IconButton
                        sx={{ mt: "-2px" }}
                        onClick={() => setPasswordMode(passwordMode === "password" ? "text" : "password")}
                    >
                        {passwordMode === "password" ? <RemoveRedEyeSharp fontSize="small" /> : <VisibilityOff fontSize="small" />}
                    </IconButton>
                ),
            }}
        />
    );
}
